type Json = any

/**
 * Resolves .NET reference-preserving JSON (Newtonsoft `PreserveReferencesHandling.Objects`)
 * into a normal object graph.
 *
 * The server emits each object once with an `$id`, and every later occurrence of the
 * same instance as a bare `{ "$ref": "<id>" }`. That keeps the wire payload acyclic, but
 * leaves the client with placeholder refs wherever the same object appears in more than
 * one path. This walks the payload and replaces each `$ref` with the real instance.
 *
 * Notes:
 *  - **Non-mutating.** Builds a brand-new graph and leaves `root` untouched, so callers
 *    that still hold the original (e.g. request/response logging) keep the acyclic,
 *    `JSON.stringify`-safe form.
 *  - **Cycle-safe.** The resolved graph intentionally contains real object cycles (that's
 *    what `$ref` encodes). Memoizing each resolved node before recursing means a `$ref`
 *    pointing back at an ancestor resolves to the same instance instead of looping.
 *  - **Identity-preserving.** Every `$ref` to a given `$id` resolves to the one shared
 *    resolved instance.
 *  - Arrays arrive as plain JSON arrays in this format (only objects carry `$id`/`$ref`),
 *    so there is no `$values` unwrapping to do.
 *  - The `$id` marker is dropped from the output; it's a serialization artifact with no
 *    domain meaning. A dangling `$ref` (no matching `$id`) is left as-is rather than
 *    silently becoming `undefined`.
 */
export function dereference(root: Json): Json {
   if (root === null || typeof root !== 'object') {
      return root
   }

   // Pass 1 — register every object that declares an $id (by original reference).
   const registry = new Map<string, Json>()
   const register = (node: Json): void => {
      if (node === null || typeof node !== 'object') return
      if (Array.isArray(node)) { node.forEach(register); return }
      if (node.$id !== undefined) registry.set(node.$id, node)
      for (const key of Object.keys(node)) register(node[key])
   }

   // Pass 2 — build the resolved graph, memoized by original-node identity so shared
   // instances (and cycles) collapse to a single resolved object.
   const built = new Map<Json, Json>()
   const build = (node: Json): Json => {
      if (node === null || typeof node !== 'object') return node
      if (Array.isArray(node)) return node.map(build)

      // A pure { "$ref": "id" } placeholder — swap in the real (resolved) instance.
      if (node.$ref !== undefined && Object.keys(node).length === 1) {
         const target = registry.get(node.$ref)
         return target === undefined ? node : build(target)
      }

      const existing = built.get(node)
      if (existing !== undefined) return existing

      const resolved: Json = {}
      built.set(node, resolved) // memoize BEFORE recursing so back-refs find this instance
      for (const key of Object.keys(node)) {
         if (key === '$id') continue // drop the serialization marker
         resolved[key] = build(node[key])
      }
      return resolved
   }

   register(root)
   return build(root)
}
